/**
 * 画布“可引用资源”候选 Hook。
 *
 * 面板打开时（enabled）独立请求当前画布模式下所有到 resourceId 粒度的业务实体
 * 可引用资源，映射为 MentionResource[]。画布节点（快照参考图/pin + 上传实拍）
 * 由调用方另行合并，不在此 hook 内。
 *
 * 必须传 genType；video 时必须传 videoMode（服务端硬过滤，与编辑页同规则）。
 */
import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

import { mapCanvasReferenceableItemToMentionResource } from '@/lib/canvas/map-canvas-referenceable-item';
import { canvasReferenceableResourcesApi } from '@/services/api/canvas-referenceable-resources';
import { useOrgContextStore } from '@/stores/orgContextStore';

import type {
  CanvasReferenceableMode,
  CanvasReferenceableResourcesQuery,
} from '@/services/api/canvas-referenceable-resources';
import type { CanvasAssetType } from '@/types/canvas';
import type { MentionResource } from '@/types/mention';

export const CANVAS_REFERENCEABLE_QUERY_KEY = 'canvas-referenceable-resources' as const;
const PERSONAL_CONTEXT_QUERY_KEY = '__personal__';

export type UseCanvasReferenceableResourcesParams = CanvasReferenceableResourcesQuery;

export type InvalidateCanvasReferenceableScope = {
  projectId: string;
  mode: CanvasReferenceableMode;
  /** storyboardId 或 assetId */
  scopeId: string;
};

/**
 * 失效指定分镜下所有画布可引用资源查询。
 * 仅 refetch active（面板/弹层已打开）；未打开的标 stale，切到对应类型再拉。
 */
export function invalidateCanvasReferenceableQueriesForStoryboard(
  queryClient: ReturnType<typeof useQueryClient>,
  storyboardId: string
): void {
  if (!storyboardId) return;
  void queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return (
        Array.isArray(key) &&
        key[0] === CANVAS_REFERENCEABLE_QUERY_KEY &&
        key[2] === 'storyboard' &&
        key[3] === storyboardId
      );
    },
    refetchType: 'active',
  });
}

/** 读取已缓存的画布可引用资源，供参考板等人像状态叠加，不发起新请求。 */
export function useCanvasReferenceableCachedMentionResources(enabled: boolean): MentionResource[] {
  const queryClient = useQueryClient();
  return useMemo(() => {
    if (!enabled) return [];
    const cached = queryClient.getQueriesData<MentionResource[]>({
      queryKey: [CANVAS_REFERENCEABLE_QUERY_KEY],
    });
    const result: MentionResource[] = [];
    for (const [, data] of cached) {
      if (!Array.isArray(data)) continue;
      result.push(...data);
    }
    return result;
  }, [enabled, queryClient]);
}

/**
 * 失效画布可引用资源缓存。
 * - 传入 scope：仅该分镜/资产前缀，且只 refetch 当前 active（面板打开）的 query
 * - 不传：失效全部，仍仅 refetch active
 */
export function useInvalidateCanvasReferenceableResources() {
  const queryClient = useQueryClient();
  return useCallback(
    (scope?: InvalidateCanvasReferenceableScope) => {
      if (scope?.mode === 'storyboard') {
        invalidateCanvasReferenceableQueriesForStoryboard(queryClient, scope.scopeId);
        return;
      }
      if (scope) {
        void queryClient.invalidateQueries({
          queryKey: [CANVAS_REFERENCEABLE_QUERY_KEY, scope.projectId, scope.mode, scope.scopeId],
          refetchType: 'active',
        });
        return;
      }
      void queryClient.invalidateQueries({
        queryKey: [CANVAS_REFERENCEABLE_QUERY_KEY],
        refetchType: 'active',
      });
    },
    [queryClient]
  );
}

/**
 * @param enabled 面板打开时才请求，避免无谓拉取。
 */
export function useCanvasReferenceableResources(
  params: UseCanvasReferenceableResourcesParams,
  options?: { enabled?: boolean }
) {
  const activeOrgId = useOrgContextStore((state) => state.activeOrgId);
  const scopeId = params.mode === 'storyboard' ? params.storyboardId : params.assetId;
  const videoModeReady = params.genType !== 'video' || Boolean(params.videoMode);

  return useQuery<MentionResource[]>({
    queryKey: [
      CANVAS_REFERENCEABLE_QUERY_KEY,
      params.projectId,
      params.mode,
      scopeId ?? '',
      params.assetType ?? '',
      params.genType,
      params.videoMode ?? '',
      // scope 必须进 key：first_frame_audio 等模式下 @ 面板（音频）与帧 picker（图片）
      // 用同一组 genType/videoMode，缺了 scope 两者会串同一份缓存。
      params.scope ?? 'mention',
      activeOrgId ?? PERSONAL_CONTEXT_QUERY_KEY,
    ] as const,
    queryFn: async () => {
      const response = await canvasReferenceableResourcesApi.list(params);
      return (response.resources ?? []).map(mapCanvasReferenceableItemToMentionResource);
    },
    enabled:
      Boolean(params.projectId) && Boolean(scopeId) && videoModeReady && (options?.enabled ?? true),
    staleTime: 1000 * 30,
    gcTime: 1000 * 60 * 5,
  });
}

export type { CanvasReferenceableMode, CanvasAssetType };
